'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/frontier'

const QuantizationViz = dynamic(() => import('../_components/widgets/FrontierWidgets').then(m => m.QuantizationViz), { ssr: false })
const MoEViz = dynamic(() => import('../_components/widgets/FrontierWidgets').then(m => m.MoEViz), { ssr: false })

export default function Efficiency() {
  return (
    <Lesson>
      <Step>
        <H>Big models are expensive to run</H>
        <P>
          A model with 70 billion parameters stored as 32-bit numbers needs 280 GB of memory just to hold its weights — and every
          generated token touches all of them. Serving millions of people makes efficiency as important as intelligence. Three
          tricks do much of the heavy lifting.
        </P>
      </Step>

      <Step gate={['q-quant', 'quantize']}>
        <H>1 · Quantization: fewer bits per number</H>
        <P>
          Weights are usually small numbers bunched around zero. Store each as an 8-bit (or even 4-bit) integer plus one shared
          scale factor, and the model shrinks 4–8× with surprisingly little loss of quality.
        </P>
        <Figure n="10.9" caption="400 weights (top) snapped onto a grid of allowed values (bottom). Fewer bits = fewer grid lines = more rounding error.">
          <QuantizationViz />
        </Figure>
        <Quiz
          id="q-quant"
          question="Going from 16-bit to 4-bit weights shrinks the model by how much?"
          options={[{ label: '4×', correct: true }, { label: '12×', why: 'It’s a ratio: 16 ÷ 4.' }, { label: '2×', why: 'That would be 16-bit to 8-bit.' }]}
        />
        <Exercise id="quantize" title="int8 quantization" {...EX.quantize} hints={[<><C>scale = max(|w|) / 127</C>; then <C>Math.round(w / scale)</C>, clamped to ±127.</>]} />
      </Step>

      <Step gate="kv-cache">
        <H>2 · The KV cache: don’t redo work</H>
        <P>
          When generating, each new token attends to all previous tokens — needing their keys and values. Those never change, so
          instead of recomputing them for every new token, store them. This <strong>KV cache</strong> turns a cost that grows like
          the square of the length into one that grows linearly.
        </P>
        <Exercise id="kv-cache" title="Count the work" {...EX.kvCache} hints={[<>Without a cache, step t processes t tokens: add up 1 + 2 + … + n.</>]} />
        <Callout kind="note">
          The cache isn’t free: it uses memory that grows with conversation length and model size — one reason very long
          contexts are expensive.
        </Callout>
      </Step>

      <Step gate="q-moe">
        <H>3 · Mixture of experts: only wake up part of the model</H>
        <P>
          Instead of one big MLP in each block, have many smaller “expert” MLPs and a tiny <strong>router</strong> network that
          picks the best 1–2 experts for each token. The model can hold far more knowledge (parameters) while each token only pays
          for a fraction of the computation.
        </P>
        <Figure n="10.10" caption="Tap a token to see which 2 of 8 experts the router sends it to.">
          <MoEViz />
        </Figure>
        <Quiz
          id="q-moe"
          question="A mixture-of-experts model has 8 experts per layer and routes each token to 2. Roughly what fraction of the expert parameters does each token use?"
          options={[{ label: 'A quarter', correct: true }, { label: 'All of them', why: 'Only the chosen 2 experts run for that token.' }, { label: 'An eighth', why: 'Two experts are chosen, not one.' }]}
        />
        <Callout kind="why">
          Other tricks: <strong>distillation</strong> (train a small model to imitate a big one), <strong>speculative
          decoding</strong> (a small model drafts tokens, the big one checks several at once), and specialised attention kernels
          that make better use of GPU memory.
        </Callout>
      </Step>
    </Lesson>
  )
}
